import {recordRumError} from './datadog/datadogRum';
import {safeSessionStorage} from './localStorage';

/**
 * Recovery for lazy chunks that fail to load: retry the URL, else reload once,
 * else fall through to the error boundary we would have hit anyway.
 *
 * The failing chunk is usually still served, the fetch just failed in transit
 * ([report matching our RUM data](https://github.com/vitejs/vite/issues/11804#issuecomment-1403473179)).
 * Re-calling the importer cannot recover that: the module map keeps the failed
 * fetch, so the same URL issues no second request
 * ([whatwg/html#6768](https://github.com/whatwg/html/issues/6768),
 * [article author on the limitation](https://github.com/vitejs/vite/issues/11804#issuecomment-1482721444)).
 * A cache-busted URL is a different key, and does request; our CDN ignores the
 * query in its cache key, so retries are served from the edge. When even that
 * fails the chunk is likely unreachable rather than unlucky
 * ([why retrying alone is not enough](https://github.com/vitejs/vite/issues/11804#issuecomment-1669265379)),
 * hence the reload that
 * [Vite's troubleshooting guide](https://github.com/sapphi-red/vite/blob/main/docs/guide/troubleshooting.md#failed-to-fetch-dynamically-imported-module-error)
 * recommends.
 */

/** Retry attempts after the initial failure. */
export const MAX_RETRIES = 2;

/** Grows per attempt, but short: a user is watching a Suspense fallback. */
const RETRY_BASE_DELAY_MS = 400;

const RELOAD_MARKER_PREFIX = 'wandb.chunkLoadReload:';

/**
 * Chromium and Firefox name the module they could not load; WebKit throws a
 * [fixed string with no URL](https://github.com/WebKit/WebKit/blob/main/Source/WebCore/bindings/js/ScriptModuleLoader.cpp#L505),
 * so its failures cannot be cache-busted and go straight to the reload path.
 * All three shapes appear in our RUM data.
 */
const FAILED_URL_PATTERNS = [
  /Failed to fetch dynamically imported module:?\s*(\S+)/,
  /error loading dynamically imported module:?\s*(\S+)/,
];

const SAFARI_MESSAGE = /Importing a module script failed/;

/**
 * Vite's own message when a chunk's CSS dependency fails to load, which rejects
 * the import just as a missing chunk does. Cache-busting cannot help: the URL it
 * names is a stylesheet, not the module whose map entry is now poisoned. Reload
 * only.
 */
const CSS_PRELOAD_MESSAGE = /Unable to preload CSS for/;

/**
 * Wrapped imports awaiting a result. The `vite:preloadError` listener fires while
 * they are still pending, so a non-zero count means the failure already has a
 * retry path and must not also reload.
 */
let pendingWrappedImports = 0;

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : '';
}

export function isDynamicImportError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return (
    SAFARI_MESSAGE.test(message) ||
    CSS_PRELOAD_MESSAGE.test(message) ||
    FAILED_URL_PATTERNS.some(pattern => pattern.test(message))
  );
}

/** The URL of the module that failed, when the browser reports it. */
export function getFailedModuleUrl(error: unknown): string | undefined {
  const message = getErrorMessage(error);
  for (const pattern of FAILED_URL_PATTERNS) {
    const match = message.match(pattern);
    if (match != null) {
      return match[1];
    }
  }
  return undefined;
}

const CACHE_BUST_PARAM = 'cacheBust';

function withCacheBust(url: string): string {
  const busted = new URL(url, window.location.href);
  busted.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
  return busted.href;
}

/**
 * The chunk's stable identity. A failure reported after a retry names the
 * cache-busted URL, whose timestamp differs on every load, so keying anything
 * per-chunk on it would never match itself again.
 */
function withoutCacheBust(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.delete(CACHE_BUST_PARAM);
    return parsed.href;
  } catch {
    return url;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function callImporter<T>(importer: () => Promise<T>): Promise<T> {
  pendingWrappedImports += 1;
  try {
    return await importer();
  } finally {
    pendingWrappedImports -= 1;
  }
}

function loadModuleUrl(url: string): Promise<unknown> {
  // A runtime URL is opaque to the bundler, hence @vite-ignore.
  return import(/* @vite-ignore */ url);
}

interface ImportWithRetryOptions {
  /** Seam for tests, which have no module server to re-request from. */
  loadModule?: (url: string) => Promise<unknown>;
}

/**
 * Runs a dynamic import, retrying a transient failure under a cache-busted URL.
 * Rejects with the last failure once retries are exhausted, so callers keep their
 * existing error handling.
 *
 * `importer` must perform exactly one dynamic import and return its module:
 * recovery re-imports the URL named in the error and returns *that* module, so a
 * loader that imports several modules, or transforms what it imported, gets back
 * the wrong value. Wrap each `import()` separately and transform outside:
 *
 * ```ts
 * await importWithRetry(() => import('./bootstrap'));
 * const module = await importWithRetry(() => import('./Component'));
 * return {default: module.Named};
 * ```
 */
export async function importWithRetry<T>(
  importer: () => Promise<T>,
  {loadModule = loadModuleUrl}: ImportWithRetryOptions = {}
): Promise<T> {
  let lastError: unknown;
  try {
    return await callImporter(importer);
  } catch (error) {
    if (!isDynamicImportError(error)) {
      throw error;
    }
    lastError = error;
  }

  const url = getFailedModuleUrl(lastError);
  for (let attempt = 1; url != null && attempt <= MAX_RETRIES; attempt++) {
    // Retrying while the browser knows it is offline just burns the delay.
    if (!window.navigator.onLine) {
      break;
    }
    await delay(RETRY_BASE_DELAY_MS * attempt + Math.random() * 100);
    try {
      // Same file the importer asked for, hence `T`, which the bundler cannot see.
      const loaded = (await callImporter(() =>
        loadModule(withCacheBust(url))
      )) as T;
      recordRumError(lastError, {
        message: 'chunk_load_recovered_by_retry',
        chunkUrl: url,
        attempt,
      });
      return loaded;
    } catch (retryError) {
      lastError = retryError;
    }
  }

  // Out of retries, or nothing retryable was named (Safari, CSS preload).
  reloadOnceForFailedChunk(lastError);
  throw lastError;
}

/**
 * Reloads once per failed chunk, for failures a retry could not fix. The marker
 * keeps a deterministically failing chunk (blocked by an extension or proxy,
 * genuinely missing) from looping: the second time we get here for that chunk we
 * do nothing and let the caller's error reach the error boundary.
 *
 * @returns whether a reload was triggered
 */
export function reloadOnceForFailedChunk(error: unknown): boolean {
  if (!isDynamicImportError(error)) {
    return false;
  }

  // Reloading with no network replaces the app with the browser's own error
  // page, which the user has to leave by hand.
  if (!window.navigator.onLine) {
    return false;
  }

  const failedUrl = getFailedModuleUrl(error);
  const chunkUrl = failedUrl == null ? undefined : withoutCacheBust(failedUrl);
  // Unavailable in some privacy modes; without a marker we cannot promise "once",
  // so prefer today's error page over a possible loop.
  if (!safeSessionStorage.isAvailable) {
    return false;
  }

  const markerKey = `${RELOAD_MARKER_PREFIX}${chunkUrl ?? getErrorMessage(error)}`;
  const alreadyReloaded = safeSessionStorage.getItem(markerKey) != null;
  safeSessionStorage.setItem(markerKey, String(Date.now()));

  if (alreadyReloaded) {
    recordRumError(error, {
      message: 'chunk_load_reload_exhausted',
      chunkUrl,
    });
    return false;
  }

  recordRumError(error, {message: 'chunk_load_reload', chunkUrl});
  window.location.reload();
  return true;
}

type PreloadErrorEvent = Event & {payload?: unknown};

let listenerInstalled = false;

/**
 * Covers the import sites `createPreloadableLazyComponent` does not: Vite dispatches
 * `vite:preloadError` for any failed dynamic import or dependency preload. It is
 * only a signal, not a recovery hook — hence the reload
 * ([Vite's own load error handling](https://vite.dev/guide/build.html#load-error-handling),
 * and [a report that the event alone does not fix
 * this](https://github.com/vitejs/vite/issues/11804#issuecomment-1682663399)).
 *
 * Deliberately not `preventDefault()`ed: that resolves the import with
 * `undefined`, turning a legible chunk error into "Cannot read properties of
 * undefined" downstream.
 */
export function initDynamicImportErrorHandling(): void {
  if (listenerInstalled) {
    return;
  }
  listenerInstalled = true;
  window.addEventListener('vite:preloadError', (event: PreloadErrorEvent) => {
    if (pendingWrappedImports > 0) {
      // `importWithRetry` owns this failure and will reload if retries fail.
      return;
    }
    reloadOnceForFailedChunk(event.payload);
  });
}
